import { Injectable } from '@angular/core';
import { concat, Observable, of, Subject, zip } from 'rxjs';
import { first, map, switchMap, tap } from 'rxjs/operators';

import {
    ClientRelation,
    ContractDataInterface,
    ContractInterface,
    ContractLiteInterface,
    CreatedContractInterface,
    IdInterface,
    OrderClientInterface,
    OrderClientRelation,
    ProductInterface,
} from '../data';
import { ContractApiRepository } from '../repositories/contract-api.repository';
import { ContractCacheRepository } from '../repositories/contract-cache.repository';
import { DocumentApiRepository } from '../repositories/document-api.repository';
import { FullClientCacheRepository } from '../repositories/full-client-cache.repository';
import { TraderApiRepository } from '../repositories/trader-api.repository';
import { Transaction } from '../transaction/transaction';
import { TransferApiRepository } from '../repositories/transfer-api.repository';
import { ValidationRepository } from '../repositories/validation.repository';

const contractFill = () =>
    map(
        (transaction: Transaction<ContractInterface>): Transaction<ContractInterface> =>
            transaction.map(
                (contract) => {
                    contract.holders.map((holder, idx) => {
                        holder.isSoleHolder =
                            contract.holders.length +
                                contract.legalGuardians.filter(
                                    (legalGuardian) => legalGuardian.legalGuardianRelation === ClientRelation.HOLDER
                                ).length ===
                            1;
                        holder.relation = ClientRelation.HOLDER;

                        holder.legalGuardians.map((legalGuardian) => {
                            legalGuardian.relation = legalGuardian.legalGuardianRelation;
                        });

                        if (idx !== 0) {
                            if (contract.legalGuardians.find((legalGuardian) => legalGuardian.id.value === holder.id.value)) {
                                contract.removeHolder(holder);
                            }
                        }
                    });

                    contract.authorized.map((authorized) => {
                        authorized.relation = ClientRelation.AUTHORIZED;
                        if (contract.legalGuardians.find((legalGuardian) => legalGuardian.id.value === authorized.id.value)) {
                            contract.removeAuthorized(authorized);
                        }
                    });
                    return Transaction.success(contract);
                },
                (throwable) => Transaction.error(throwable.message)
            )
    );

const contractDocuments = (documentApiRepository: DocumentApiRepository) =>
    switchMap(
        (transaction: Transaction<ContractInterface>): Observable<Transaction<ContractInterface>> =>
            transaction.map(
                (contract) => {
                    if (contract.encryptedCode) {
                        return documentApiRepository.loadContract(contract.encryptedCode).pipe(
                            map((transactionDocument) =>
                                transactionDocument.map(
                                    (documents) => {
                                        contract.documents = documents;
                                        return Transaction.success(contract);
                                    },
                                    (_) => transaction
                                )
                            )
                        );
                    }

                    return of(Transaction.success(contract));
                },
                (throwable) => of(Transaction.error(throwable.message))
            )
    );

const contractValidation = (validationRepository: ValidationRepository) =>
    switchMap(
        (transaction: Transaction<ContractInterface>): Observable<Transaction<ContractInterface>> =>
            transaction.map(
                (contract) => {
                    if (contract.code) {
                        return validationRepository.load(contract.code).pipe(
                            map((transactionValidation) => {
                                if (transactionValidation.isSuccess) {
                                    contract.validation = transactionValidation.data;
                                }

                                return Transaction.success(contract);
                            })
                        );
                    }

                    return of(Transaction.success(contract));
                },
                (throwable) => of(Transaction.error(throwable.message))
            )
    );

const contractTransfer = (traderApiRepository: TraderApiRepository) =>
    switchMap(
        (transaction: Transaction<ContractInterface>): Observable<Transaction<ContractInterface>> =>
            transaction.map(
                (contract) => {
                    if (contract.transfer) {
                        contract.transfer.code = contract.encryptedCode;
                        return traderApiRepository.load().pipe(
                            map((transactionTrader) => {
                                if (transactionTrader.isSuccess) {
                                    contract.transfer.fundTransfers.forEach((fundTransfer) => {
                                        const manager = transactionTrader.data.find(
                                            (trader) => trader.id.value === fundTransfer.externalTransferManager
                                        );
                                        if (manager) {
                                            fundTransfer.externalTransferManagerName = manager.name;
                                        }
                                    });
                                }

                                return Transaction.success(contract);
                            })
                        );
                    }

                    return of(Transaction.success(contract));
                },
                (throwable) => of(Transaction.error(throwable.message))
            )
    );

@Injectable()
export class ContractProvider {
    private semaphore$: Subject<Transaction<ContractInterface>>;
    private currentInProgress = false;

    constructor(
        private contractApiRepository: ContractApiRepository,
        private contractCacheRepository: ContractCacheRepository,
        private documentApiRepository: DocumentApiRepository,
        private fullClientCacheRepository: FullClientCacheRepository,
        private traderApiRepository: TraderApiRepository,
        private transferApiRepository: TransferApiRepository,
        private validationRepository: ValidationRepository
    ) {}

    all(clientCode: IdInterface): Observable<Transaction<ContractLiteInterface[]>> {
        return this.contractApiRepository.loadAll(clientCode);
    }

    cancel(contract: ContractInterface): Observable<Transaction<void>> {
        return this.contractApiRepository.cancel(contract).pipe(
            tap((transaction) => {
                if (transaction.isSuccess) {
                    this.contractCacheRepository.clear().subscribe();
                }
            })
        );
    }

    create(contract: ContractInterface): Observable<Transaction<void>> {
        return this.contractApiRepository.create(contract).pipe(
            tap((transaction) => {
                if (transaction.isSuccess) {
                    this.contractCacheRepository.clear().subscribe();
                    this.fullClientCacheRepository.clear().subscribe();
                }
            })
        );
    }

    createWithProducts(
        contract: ContractInterface,
        products: ProductInterface[],
        captcha: string
    ): Observable<Transaction<CreatedContractInterface>> {
        return this.contractApiRepository.createWithProducts(contract, products, captcha).pipe(
            tap((transaction) => {
                if (transaction.isSuccess && transaction.data) {
                    this.contractCacheRepository.clear().subscribe();
                    this.fullClientCacheRepository.clear().subscribe();
                }
            })
        );
    }

    current(): Observable<Transaction<ContractInterface>> {
        if (this.currentInProgress) {
            return this.semaphore$.asObservable();
        } else {
            this.semaphore$ = new Subject<any>();
            this.currentInProgress = true;

            return concat(
                this.contractCacheRepository.load(),
                this.contractApiRepository.load().pipe(
                    contractDocuments(this.documentApiRepository),
                    tap((transaction: Transaction<ContractInterface>) => {
                        if (transaction.isSuccess) {
                            this.contractCacheRepository.save(transaction.data).subscribe();
                        }
                    })
                )
            ).pipe(
                first(),
                contractFill(),
                contractValidation(this.validationRepository),
                contractTransfer(this.traderApiRepository),
                tap((transaction) => {
                    setTimeout(() => {
                        this.currentInProgress = false;
                        this.semaphore$.next(transaction);
                        this.semaphore$.complete();
                    }, 1);
                })
            );
        }
    }

    data(contractCode: IdInterface): Observable<Transaction<ContractDataInterface>> {
        return this.contractApiRepository.loadData(contractCode).pipe(
            switchMap((transaction) =>
                transaction.map(
                    (data) => {
                        if (data.modelId) {
                            return this.transferApiRepository.getModel(data.modelId).pipe(
                                map((transactionModel) =>
                                    transactionModel.map(
                                        (model) => {
                                            data.model = model;
                                            return Transaction.success(data);
                                        },
                                        (throwable) => Transaction.error(throwable.message)
                                    )
                                )
                            );
                        }

                        return of(Transaction.success(data));
                    },
                    (throwable) => of(Transaction.error(throwable.message))
                )
            )
        );
    }

    finalize(contract: ContractInterface): Observable<Transaction<void>> {
        return this.contractApiRepository.finalize(contract).pipe(
            tap((transaction) => {
                if (transaction.isSuccess) {
                    this.contractCacheRepository.clear().subscribe();
                }
            })
        );
    }

    reject(contract: ContractInterface, message: string): Observable<Transaction<void>> {
        return this.contractApiRepository.reject(contract, message).pipe(
            tap((transaction) => {
                if (transaction.isSuccess) {
                    this.contractCacheRepository.clear().subscribe();
                }
            })
        );
    }

    revise(contract: ContractInterface, message: string): Observable<Transaction<void>> {
        return this.contractApiRepository.revise(contract, message).pipe(
            tap((transaction) => {
                if (transaction.isSuccess) {
                    this.contractCacheRepository.clear().subscribe();
                }
            })
        );
    }

    signatories(contractCode: IdInterface, clientCode?: IdInterface): Observable<Transaction<OrderClientInterface[]>> {
        return this.contractApiRepository.loadSignatories(contractCode, clientCode).pipe(
            tap((transaction) =>
                transaction.map((signatories) => {
                    signatories.forEach((it) => (it.isSoleHolder = signatories.length === 1));
                })
            )
        );
    }

    update(contract: ContractInterface): Observable<Transaction<void>> {
        if (!contract.encryptedCode) {
            return new Observable((observer) => {
                this.contractCacheRepository.save(contract).subscribe({
                    complete: () => {
                        observer.next(Transaction.success());
                        observer.complete();
                    },
                });
            });
        } else {
            return new Observable((observer) => {
                this.contractApiRepository
                    .update(contract)
                    .pipe(
                        tap((transaction) => {
                            if (transaction.isSuccess) {
                                this.contractCacheRepository.clear().subscribe();
                            }
                        })
                    )
                    .subscribe((transaction) => {
                        if (transaction.isSuccess) {
                            const uploadDocuments$: Observable<Transaction<void>>[] = this.getUploadDocuments(contract);

                            if (uploadDocuments$.length > 0) {
                                concat(...uploadDocuments$)
                                    .pipe(
                                        tap((transactionUpload: Transaction<void>) => {
                                            if (!transactionUpload.isSuccess) {
                                                throw Error(transactionUpload.error);
                                            }
                                        })
                                    )
                                    .subscribe({
                                        error: (error) => {
                                            observer.next(Transaction.error(error.message));
                                            observer.complete();
                                        },
                                        complete: () => {
                                            observer.next(transaction);
                                            observer.complete();
                                        },
                                    });
                            } else {
                                observer.next(transaction);
                                observer.complete();
                            }
                        } else {
                            observer.next(transaction);
                            observer.complete();
                        }
                    });
            });
        }
    }

    protected getUploadDocuments(contract: ContractInterface): Observable<Transaction<void>>[] {
        const observables$: Observable<Transaction<void>>[] = [];

        contract.documents.map((document) => {
            if (document.id && document.remove) {
                observables$.push(this.documentApiRepository.remove(document));
            }

            if (document.file instanceof File || (document.file as Blob) instanceof Blob) {
                observables$.push(this.documentApiRepository.upload(contract.encryptedCode, document));
            }
        });

        return observables$;
    }

    validate(contract: ContractInterface, validateDocument = true): Observable<Transaction<void>> {
        return new Observable((observer) => {
            let error: string;

            const validateDocuments$: Observable<Transaction<void>>[] = [];

            if (validateDocument) {
                contract.documents.map((document) => {
                    if (document.id) {
                        validateDocuments$.push(this.documentApiRepository.validate(contract.encryptedCode, document));
                    }
                });
            }

            if (validateDocuments$.length > 0) {
                zip(...validateDocuments$)
                    .pipe(
                        tap((transactions: Transaction<void>[]) => {
                            transactions.map((transaction) => {
                                if (!transaction.isSuccess) {
                                    error = transaction.error;
                                }
                            });
                        })
                    )
                    .subscribe({
                        complete: () => {
                            if (error) {
                                observer.next(Transaction.error(error));
                                observer.complete();
                            } else {
                                this.validateContract(contract).subscribe((transaction) => {
                                    observer.next(transaction);
                                    observer.complete();
                                });
                            }
                        },
                    });
            } else {
                this.validateContract(contract).subscribe((transaction) => {
                    observer.next(transaction);
                    observer.complete();
                });
            }
        });
    }

    private validateContract(contract: ContractInterface): Observable<Transaction<void>> {
        return this.contractApiRepository.validate(contract).pipe(
            tap((transaction) => {
                if (transaction.isSuccess) {
                    this.contractCacheRepository.clear().subscribe();
                }
            })
        );
    }

    validateNotification(contract: ContractInterface): Observable<Transaction<void>> {
        return this.contractApiRepository.validateNotification(contract);
    }
}
